import React from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import clsx from 'clsx';

const ProgressDashboard: React.FC = () => {
  const history = useSelector((state: RootState) => state.evaluation.history);

  if (history.length === 0) return null;

  const totalSessions = history.length;
  const averageScore =
    history.reduce((sum, s) => sum + s.overallScore, 0) / totalSessions;

  const recentSessions = history.slice(0, 5);

  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-4">Session Progress</h3>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-100 dark:bg-gray-800/50 rounded-xl p-4 text-center">
          <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{totalSessions}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Total Sessions</p>
        </div>
        <div className="bg-gray-100 dark:bg-gray-800/50 rounded-xl p-4 text-center">
          <p
            className={clsx('text-3xl font-bold', {
              'text-emerald-600 dark:text-emerald-400': averageScore >= 80,
              'text-yellow-500 dark:text-yellow-400': averageScore >= 60 && averageScore < 80,
              'text-red-500 dark:text-red-400': averageScore < 60,
            })}
          >
            {Math.round(averageScore)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Average Score</p>
        </div>
      </div>

      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wider">
        Recent Sessions
      </h4>
      <div className="space-y-2">
        {recentSessions.map((session, idx) => {
          const scoreColor =
            session.overallScore >= 80
              ? 'text-emerald-600 dark:text-emerald-400'
              : session.overallScore >= 60
              ? 'text-yellow-500 dark:text-yellow-400'
              : 'text-red-500 dark:text-red-400';

          return (
            <div
              key={session.sessionId || idx}
              className="flex items-center justify-between bg-gray-100 dark:bg-gray-800/30 rounded-lg px-4 py-3"
            >
              <div>
                <span className="text-sm text-gray-700 dark:text-gray-200 font-medium">
                  {session.surah}:{session.ayah}
                </span>
                <span className="text-xs text-gray-500 ml-2">
                  {new Date(session.createdAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
              <span className={clsx('text-lg font-bold', scoreColor)}>
                {Math.round(session.overallScore)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProgressDashboard;
